import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";

type BulkQueryCacheContext = {
  queryClient: QueryClient;
  populatedKeys: Set<string>;
};

type UseBulkQueryCacheParams<TData> = {
  isBulkLoading: boolean;
  bulkData: TData | undefined | null;
  isBulkEnabled?: boolean;
  initialIds?: string[];
  currentIds?: string[];
  onPopulateCache: (data: NonNullable<TData>, context: BulkQueryCacheContext) => void;
};

export const useBulkQueryCache = <TData>({
  isBulkLoading,
  bulkData,
  isBulkEnabled = true,
  initialIds = [],
  currentIds = [],
  onPopulateCache,
}: UseBulkQueryCacheParams<TData>) => {
  const queryClient = useQueryClient();
  const populatedCacheRef = useRef<Set<string>>(new Set());
  const onPopulateCacheRef = useRef(onPopulateCache);
  onPopulateCacheRef.current = onPopulateCache;

  const hasItemsToFetch = currentIds.length > 0;
  const isWaitingForInitialIds = hasItemsToFetch && initialIds.length === 0;
  const hasNothingToFetch = !isBulkEnabled || (initialIds.length === 0 && currentIds.length === 0);

  const [isCachePopulated, setIsCachePopulated] = useState(() => hasNothingToFetch);

  const isInitialLoading =
    isWaitingForInitialIds ||
    (isBulkEnabled && isBulkLoading) ||
    (isBulkEnabled && !isCachePopulated);

  useLayoutEffect(() => {
    if (bulkData) {
      onPopulateCacheRef.current(bulkData, {
        queryClient,
        populatedKeys: populatedCacheRef.current,
      });
      setIsCachePopulated(true);
    }
  }, [bulkData, queryClient]);

  return { isInitialLoading };
};
